import axios from "axios";
import { baseUrl } from "@/consts";
import { Cart, CartProduct, ProcessPrecommitmentResponse, Product } from "@/interfaces";
import {
  combinePaths,
  isNonEmptyArray,
  isNonEmptyNumber,
  isNonEmptyString,
  roundCurrency,
} from "@/utils";
import { boolean } from "yup";

export const getItemInStoreQuantity = (item?: CartProduct): number =>
  item ? item.quantity || 0 : 0;

export const getItemShippedQuantity = (item?: CartProduct): number =>
  item ? item.shippingQuantity || 0 : 0;

export const getItemSubtotal = (item?: CartProduct): number => {
  if (!item) return 0;

  const price = item.price || 0;
  const inStoreQty = getItemInStoreQuantity(item);
  const shippedQty = getItemShippedQuantity(item);

  return roundCurrency(price * (inStoreQty + shippedQty));
};

export const getItemInStoreSubtotal = (item?: CartProduct): number => {
  return item
    ? roundCurrency((item.price || 0) * getItemInStoreQuantity(item))
    : 0;
};

export const getItemShippedSubtotal = (item?: CartProduct): number => {
  return item
    ? roundCurrency((item.price || 0) * getItemShippedQuantity(item))
    : 0;
};

export const getItemBalance = (item?: CartProduct, cart?: Cart): number => {
  return item
    ? roundCurrency(getItemSubtotal(item) - (item.coopApplied || 0))
    : 0;
};

export const getItemInStoreBalance = (item?: CartProduct): number => {
  return item
    ? roundCurrency(
        getItemInStoreSubtotal(item) -
          roundCurrency(getItemCoopApplied(item) * getItemInStoreQuantity(item))
      )
    : 0;
};

export const getItemShippedBalance = (item?: CartProduct): number => {
  return item
    ? roundCurrency(
        getItemShippedSubtotal(item) -
          roundCurrency(getItemCoopApplied(item) * getItemShippedQuantity(item))
      )
    : 0;
};

export const getItemCoopApplied = (item?: CartProduct): number => {
  return (
    (item &&
      (isNonEmptyNumber(item.coopApplied) && isNonEmptyNumber(item.quantity)
        ? roundCurrency((item.coopApplied || 0) / (item.quantity || 1))
        : isNonEmptyNumber(item?.shippingQuantity)
        ? roundCurrency((item.coopApplied || 0) / (item?.shippingQuantity || 1))
        : 0)) ||
    0
  );
};

export const getSubtotalCoopTotal = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc, item) => roundCurrency(acc + (item?.coopApplied || 0)),
        0
      )
    : 0;
};

export const getSubtotalBalance = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc, item) => roundCurrency(acc + getItemBalance(item, cart)),
        0
      )
    : 0;
};

export const getCouponTotal = (cart?: Cart): number => {
  return roundCurrency((cart?.couponQuantity || 0) * (cart?.couponAmount || 0));
};

export const hasInStoreItems = (cart?: Cart): boolean => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.some((item) => item.quantity || 0)
    : false;
};

export const getInStoreItems = (cart?: Cart) => {
  if (!cart || !Array.isArray(cart.products)) {
    return [];
  }

  return cart.products.filter((item) => item.quantity || 0);
};

export const getInStoreItemsQuantity = (cart?: Cart | CartProduct[]) => {
  return (!Array.isArray(cart) ? getInStoreItems(cart) : cart).reduce(
    (acc, item) => acc + (item.quantity || 0),
    0
  );
};

export const hasShippedItems = (cart?: Cart): boolean => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.some((item) =>
        isNonEmptyNumber(item.shippingQuantity as number)
      )
    : false;
};

export const getShippedItems = (cart?: Cart) => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.filter((item) =>
        isNonEmptyNumber(item.shippingQuantity as number)
      )
    : [];
};

export const getShippedItemsQuantity = (cart?: Cart | CartProduct[]) => {
  return (!Array.isArray(cart) ? getShippedItems(cart) : cart).reduce(
    (acc, item) => acc + (item.shippingQuantity || 0),
    0
  );
};

export const getShippingFee = (cart?: Cart): number => {
  return (!hasOffsiteProducts(cart) && hasShippedItems(cart)) ||
    hasOffsiteProducts(cart)
    ? cart?.shippingFee || 0
    : 0;
};

export const getTotalAmount = (cart?: Cart, isOffSite?: boolean): number => {
  const result = roundCurrency(
    getSubtotalBalance(cart) -
      (isOffSite ? cart?.offSiteDiscount || 0 : cart?.discount || 0) -
      getCouponTotal(cart) +
      getShippingFee(cart)
  );

  return result >= 0 ? result : 0;
};


// export const isProductAvaiable = (product:CartProduct,increment:number)=>{
//   if(product.inventoryCount > )
// }


export const getTotalQuantity = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc: number, item: CartProduct) =>
          acc + (item.shippingQuantity || 0) + (item.quantity || 0),
        0
      )
    : 0;
};

export const getCartSubtotal = (cart?: Cart): number => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.reduce((acc: number, item: CartProduct) => {
        // For items that have shippingQuantity > 0, use shippingQuantity
        if ((item.shippingQuantity || item.quantity || 0) > 0) {
          return (
            acc +
            roundCurrency((item.price || 0) * (item.shippingQuantity || 0)) +
            roundCurrency((item.price || 0) * (item.quantity || 0))
          );
        }
        // For in-store items, use quantity minus shippingQuantity
        return (
          acc +
          roundCurrency(
            (item.price || 0) *
              ((item.quantity || 0) - (item.shippingQuantity || 0))
          )
        );
      }, 0)
    : 0;
};

export const recalculateCoop = (
  cart?: Cart,
  coopAvailableBalance?: number,
  acbAllowanceEnabled?:boolean
): Cart | undefined => {
  console.log("➡️ Recalculating Co-Op (split: in-store vs shipping)...");

  if (
    !Array.isArray(cart?.products) ||
    typeof coopAvailableBalance !== "number" ||
    coopAvailableBalance <= 0 

  ) {
    console.warn("❌ Invalid cart or coopAvailableBalance", {
      cart,
      coopAvailableBalance,
      
    });
    return cart;
  }

  const coopEligible = cart.coopEligible || 0;

  let totalEligibleCoop = 0;

  // Calculate the total eligible coop based on both in-store and shipping
  cart.products.forEach((item) => {
    const inStoreSubtotal = (item.price || 0) * (item.quantity || 0);
    const shippingSubtotal = (item.price || 0) * (item.shippingQuantity || 0);
    totalEligibleCoop += roundCurrency(
      (inStoreSubtotal + shippingSubtotal) * coopEligible
    );
  });

  const scalingFactor =
    totalEligibleCoop > coopAvailableBalance
      ? coopAvailableBalance / totalEligibleCoop
      : 1;

  console.log("🧾 Total Eligible Coop:", totalEligibleCoop);
  console.log("💡 Scaling Factor:", scalingFactor);


  // Apply coop separately
  cart.products.forEach((item, index) => {
    const price = item.price || 0;
    const qtyInStore = item.quantity || 0;
    const qtyShipping = item.shippingQuantity || 0;

    const inStoreSubtotal = price * qtyInStore;
    const shippingSubtotal = price * qtyShipping;


      const appliedCoopInStore = roundCurrency(
        inStoreSubtotal * coopEligible * scalingFactor
      );
      const appliedCoopShipping = roundCurrency(
        shippingSubtotal * coopEligible * scalingFactor
      );
      
          item.coopInStore = appliedCoopInStore;
          item.coopShipping = appliedCoopShipping;
          item.coop = item.coopApplied = appliedCoopInStore + appliedCoopShipping;


          console.log(
            `🔹 [${index}] ${item.productDescription} (ID: ${item.productId})`
          );
          console.log(
            "   ↳ In-Store Qty:",
            qtyInStore,
            "Subtotal:",
            inStoreSubtotal,
            "Co-Op:",
            appliedCoopInStore
          );
          console.log(
            "   ↳ Shipping Qty:",
            qtyShipping,
            "Subtotal:",
            shippingSubtotal,
            "Co-Op:",
            appliedCoopShipping
          );
    


    console.log("   ↳ Total Coop Applied:", item.coopApplied);
  });

  return cart;
};

export const hasOffsiteProducts = (cart?: Cart) => {
  return Array.isArray(cart?.products)
    ? cart?.products?.some((item) => item.offsite === true)
    : false;
};

export const isEmpty = (cart?: Cart): boolean => {
  return (
    !cart ||
    !Array.isArray(cart?.products) ||
    (cart?.products?.length as number) === 0
  );
};

export const hasShippingAddress = (cart?: Cart): boolean =>
  isNonEmptyString(cart?.shippingData?.shippingAddress?.street) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.city) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.state) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.zip);

export const hasShippingContact = (cart?: Cart): boolean =>
  isNonEmptyString(cart?.shippingData?.shippingContact?.firstName) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.lastName) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.email) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.phone);

export const hasShippingData = (cart?: Cart): boolean =>
  hasShippingAddress(cart) || hasShippingContact(cart);

export const processPreCommitmentAsync = (
  masterDealerId?: string,
  offsite?: boolean
) =>
  axios.post<ProcessPrecommitmentResponse>(
    combinePaths(baseUrl.ORDER, "processprecommitment") as string,
    {
      masterDealerId,
      offsite,
    }
  );
